import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { APP } from './tokens';
import type {
  NotionTarget,
  NotionDatabaseSchemaData,
  NotionDatabasePropertySchema,
} from '@corner/shared';

// ─── Props ──────────────────────────────────────

interface NotionDatabaseEntryModalProps {
  onSave: (entryTitle: string, databaseName: string) => void;
  onClose: () => void;
  getToken?: () => string | null;
  title?: string;
  content?: string;
}

// ─── Property type icons ────────────────────────

function PropertyIcon({ type }: { type: string }) {
  const s = { flexShrink: 0 } as const;
  const c = '#9A8E82';
  switch (type) {
    case 'status':
    case 'select':
      return <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={s}><circle cx="7" cy="7" r="5.5" stroke={c} strokeWidth="1.2"/><path d="M4.5 7h5" stroke={c} strokeWidth="1.2" strokeLinecap="round"/></svg>;
    case 'date':
      return <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={s}><rect x="2" y="3" width="10" height="9" rx="1.5" stroke={c} strokeWidth="1.2"/><path d="M2 6h10M5 1.5v3M9 1.5v3" stroke={c} strokeWidth="1.2" strokeLinecap="round"/></svg>;
    case 'multi_select':
      return <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={s}><path d="M2 3.5h4l5 5-3.5 3.5-5-5V3.5z" stroke={c} strokeWidth="1.2" strokeLinejoin="round"/><circle cx="5" cy="6" r="1" fill={c}/></svg>;
    case 'people':
      return <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={s}><circle cx="7" cy="5" r="2.5" stroke={c} strokeWidth="1.2"/><path d="M3 12c0-2.2 1.8-4 4-4s4 1.8 4 4" stroke={c} strokeWidth="1.2" strokeLinecap="round"/></svg>;
    case 'rich_text':
      return <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={s}><path d="M3 4h8M3 7h5M3 10h6" stroke={c} strokeWidth="1.2" strokeLinecap="round"/></svg>;
    case 'number':
      return <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={s}><path d="M5 2v10M9 2v10M2 5h10M2 9h10" stroke={c} strokeWidth="1.2" strokeLinecap="round"/></svg>;
    case 'checkbox':
      return <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={s}><rect x="2" y="2" width="10" height="10" rx="2" stroke={c} strokeWidth="1.2"/><path d="M4.5 7l2 2 3.5-4" stroke={c} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case 'url':
      return <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={s}><path d="M6 8l2-2M5 9.5A2.5 2.5 0 013.5 5L5 3.5M9 4.5A2.5 2.5 0 0110.5 9L9 10.5" stroke={c} strokeWidth="1.2" strokeLinecap="round"/></svg>;
    case 'email':
      return <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={s}><rect x="2" y="3.5" width="10" height="7" rx="1.5" stroke={c} strokeWidth="1.2"/><path d="M2 4.5l5 3.5 5-3.5" stroke={c} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case 'phone_number':
      return <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={s}><rect x="4" y="1.5" width="6" height="11" rx="1.5" stroke={c} strokeWidth="1.2"/><path d="M6 10.5h2" stroke={c} strokeWidth="1.2" strokeLinecap="round"/></svg>;
    default:
      return <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={s}><circle cx="7" cy="7" r="5.5" stroke={c} strokeWidth="1.2"/></svg>;
  }
}

// ─── Notion color map ───────────────────────────

const NOTION_COLORS: Record<string, { bg: string; text: string }> = {
  default: { bg: '#E8DED4', text: '#5C504A' },
  gray: { bg: '#E3E2E0', text: '#6B6966' },
  brown: { bg: '#EEE0DA', text: '#7A5C47' },
  orange: { bg: '#FADEC9', text: '#D97645' },
  yellow: { bg: '#FDECC8', text: '#997B40' },
  green: { bg: '#DBEDDB', text: '#3B7A4B' },
  blue: { bg: '#D3E5EF', text: '#3A76A5' },
  purple: { bg: '#E8DEEE', text: '#7B5EA7' },
  pink: { bg: '#F5E0E9', text: '#AD4E7C' },
  red: { bg: '#FFE2DD', text: '#C0392B' },
};

function getNotionColor(color?: string | null) {
  return NOTION_COLORS[color ?? 'default'] ?? NOTION_COLORS.default;
}

// ─── Main component ─────────────────────────────

export default function NotionDatabaseEntryModal({
  onSave,
  onClose,
  getToken,
  title = '',
}: NotionDatabaseEntryModalProps) {
  const [databases, setDatabases] = useState<NotionTarget[]>([]);
  const [selectedDb, setSelectedDb] = useState<NotionTarget | null>(null);
  const [dbPickerOpen, setDbPickerOpen] = useState(false);
  const [schema, setSchema] = useState<NotionDatabaseSchemaData | null>(null);
  const [pageTitle, setPageTitle] = useState(title);
  const [propertyValues, setPropertyValues] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingProp, setEditingProp] = useState<string | null>(null);

  const headers = useCallback(() => {
    const token = getToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  // Fetch databases on mount
  useEffect(() => {
    setLoading(true);
    axios.get<{ results: NotionTarget[] }>('/api/integrations/notion/targets?type=database', { headers: headers() })
      .then((res) => {
        const dbs = res.data.results ?? [];
        setDatabases(dbs);
        if (dbs.length > 0) setSelectedDb(dbs[0]);
      })
      .catch(() => { /* silent */ })
      .finally(() => setLoading(false));
  }, [headers]);

  // Fetch schema when database changes
  useEffect(() => {
    if (!selectedDb) { setSchema(null); return; }
    axios.get<NotionDatabaseSchemaData>(`/api/integrations/notion/databases/${selectedDb.id}/schema`, { headers: headers() })
      .then((res) => {
        setSchema(res.data);
        setPropertyValues({});
      })
      .catch(() => setSchema(null));
  }, [selectedDb, headers]);

  const editableProps = schema?.properties.filter((p) => !p.readOnly && p.type !== 'title') ?? [];
  const titleProp = schema?.properties.find((p) => p.type === 'title');
  const filledCount = Object.keys(propertyValues).filter((k) => propertyValues[k] != null && propertyValues[k] !== '').length;

  const handleSave = useCallback(async () => {
    if (!selectedDb || !schema) return;
    setSaving(true);
    try {
      // Build Notion-formatted properties
      const props: Record<string, unknown> = {};
      // Title property
      if (titleProp) {
        props[titleProp.name] = { title: [{ text: { content: pageTitle } }] };
      }
      // Other properties
      for (const [name, value] of Object.entries(propertyValues)) {
        if (value == null || value === '') continue;
        const propSchema = editableProps.find((p) => p.name === name);
        if (!propSchema) continue;
        switch (propSchema.type) {
          case 'select':
          case 'status':
            props[name] = { [propSchema.type]: { name: value } };
            break;
          case 'multi_select':
            props[name] = { multi_select: (value as string[]).map((v) => ({ name: v })) };
            break;
          case 'date':
            props[name] = { date: { start: value } };
            break;
          case 'rich_text':
            props[name] = { rich_text: [{ text: { content: value } }] };
            break;
          case 'number':
            props[name] = { number: Number(value) };
            break;
          case 'checkbox':
            props[name] = { checkbox: Boolean(value) };
            break;
          case 'url':
            props[name] = { url: String(value) };
            break;
          case 'email':
            props[name] = { email: String(value) };
            break;
          case 'phone_number':
            props[name] = { phone_number: String(value) };
            break;
          default:
            props[name] = value;
        }
      }

      await axios.post('/api/integrations/notion/database-entry', {
        databaseId: selectedDb.id,
        properties: props,
      }, { headers: headers() });

      onSave(pageTitle, selectedDb.title);
    } catch {
      toast({ title: 'Failed to save', description: 'Could not create Notion entry.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }, [selectedDb, schema, titleProp, pageTitle, propertyValues, editableProps, headers, onSave]);

  const setPropValue = useCallback((name: string, value: unknown) => {
    setPropertyValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  return (
    <div style={MODAL_SHELL}>
      {/* Header */}
      <div style={HEADER}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/IntPointsLogos/notion-icon.webp" alt="Notion" style={{ width: 20, height: 20, borderRadius: 3 }} />
          <span style={{ fontSize: 15, fontWeight: 600, color: '#2E2318', lineHeight: '18px' }}>Add to Notion database</span>
        </div>
        <button type="button" onClick={onClose} style={CLOSE_BTN}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4L12 12M12 4L4 12" stroke="#9A8E82" strokeWidth="1.5" strokeLinecap="round"/></svg>
        </button>
      </div>

      {/* Database picker */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '16px 20px 12px' }}>
        <span style={LABEL}>Database</span>
        {loading ? (
          <div style={{ ...PICKER, color: '#9A8E82' }}>Loading databases...</div>
        ) : (
          <div style={{ position: 'relative' }}>
            <button type="button" onClick={() => setDbPickerOpen(!dbPickerOpen)} style={PICKER}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                <div style={{ width: 20, height: 20, borderRadius: 4, backgroundColor: '#4285F4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><rect x="1" y="1" width="10" height="10" rx="1.5" stroke="#FFF" strokeWidth="1.2"/><path d="M1 5h10M5 5v6" stroke="#FFF" strokeWidth="1"/></svg>
                </div>
                <span style={{ fontSize: 14, fontWeight: 500, color: '#2E2318' }}>{selectedDb?.title ?? 'Select database'}</span>
              </div>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M4 5.5L7 8.5L10 5.5" stroke="#9A8E82" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            {dbPickerOpen && databases.length > 0 && (
              <div style={DROPDOWN}>
                {databases.map((db) => (
                  <button key={db.id} type="button" onClick={() => { setSelectedDb(db); setDbPickerOpen(false); }} style={{ ...DROPDOWN_ITEM, fontWeight: db.id === selectedDb?.id ? 500 : 400 }}>
                    {db.iconEmoji && <span style={{ fontSize: 14 }}>{db.iconEmoji}</span>}
                    <span style={{ flex: 1 }}>{db.title}</span>
                    {db.id === selectedDb?.id && <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 7.5l3 3 5-6" stroke="#D97645" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Page title */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '0 20px 16px' }}>
        <span style={LABEL}>Page title</span>
        <input
          type="text"
          value={pageTitle}
          onChange={(e) => setPageTitle(e.target.value)}
          style={TITLE_INPUT}
          placeholder="Untitled"
        />
      </div>

      {/* Divider */}
      <div style={{ height: 1, backgroundColor: '#EDE8E2' }} />

      {/* Properties */}
      {schema && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px 8px' }}>
            <span style={LABEL}>Properties</span>
            <span style={{ fontSize: 11, fontWeight: 400, color: '#B8ACA2', lineHeight: '14px' }}>{filledCount} of {editableProps.length} mapped</span>
          </div>

          {editableProps.map((prop) => (
            <PropertyRow
              key={prop.id}
              prop={prop}
              value={propertyValues[prop.name]}
              editing={editingProp === prop.name}
              onEdit={() => setEditingProp(editingProp === prop.name ? null : prop.name)}
              onChange={(v) => { setPropValue(prop.name, v); setEditingProp(null); }}
            />
          ))}

          {/* AI hint */}
          <div style={AI_HINT}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M7 2L8.2 5.8L12 7L8.2 8.2L7 12L5.8 8.2L2 7L5.8 5.8Z" fill="#D97645"/></svg>
            <span style={{ fontSize: 12, color: '#8B7355', lineHeight: '16px' }}>Properties were auto-filled from the document. Edit any value before saving.</span>
          </div>
        </>
      )}

      {/* Footer */}
      <div style={FOOTER}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: 0.5 }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1v10M1 6h10" stroke="#B8ACA2" strokeWidth="1.2" strokeLinecap="round"/></svg>
          <span style={{ fontSize: 12, color: '#B8ACA2' }}>Attach file</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#9A8E82', fontFamily: F }}>Cancel</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !selectedDb}
            style={{ ...SAVE_BTN, opacity: saving || !selectedDb ? 0.5 : 1 }}
          >
            {saving ? 'Saving...' : 'Save to Notion'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Property row ───────────────────────────────

function PropertyRow({
  prop,
  value,
  editing,
  onEdit,
  onChange,
}: {
  prop: NotionDatabasePropertySchema;
  value: unknown;
  editing: boolean;
  onEdit: () => void;
  onChange: (v: unknown) => void;
}) {
  const hasValue = value != null && value !== '' && (Array.isArray(value) ? value.length > 0 : true);

  return (
    <div style={PROP_ROW}>
      <div style={PROP_LABEL}>
        <PropertyIcon type={prop.type} />
        <span style={{ fontSize: 13, color: '#6B5E52', lineHeight: '16px' }}>{prop.name}</span>
      </div>

      {hasValue && !editing ? (
        <button type="button" onClick={onEdit} style={PROP_VALUE_FILLED}>
          {prop.type === 'select' || prop.type === 'status' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#D97645' }} />
              <span style={{ fontSize: 13, color: '#2E2318' }}>{String(value)}</span>
            </div>
          ) : prop.type === 'multi_select' && Array.isArray(value) ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(value as string[]).map((tag, i) => {
                const colors = Object.values(NOTION_COLORS);
                const c = colors[i % colors.length];
                return <span key={tag} style={{ padding: '4px 10px', borderRadius: 4, backgroundColor: c.bg, fontSize: 12, fontWeight: 500, color: c.text, lineHeight: '16px' }}>{tag}</span>;
              })}
            </div>
          ) : (
            <span style={{ fontSize: 13, color: '#2E2318' }}>{String(value)}</span>
          )}
        </button>
      ) : editing ? (
        <div style={{ flex: 1 }}>
          {(prop.type === 'select' || prop.type === 'status') && prop.options ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, border: `1px solid ${APP.border}`, borderRadius: 6, overflow: 'hidden', maxHeight: 160, overflowY: 'auto' }}>
              {prop.options.map((opt) => {
                const c = getNotionColor(opt.color);
                return (
                  <button key={opt.name} type="button" onClick={() => onChange(opt.name)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: F, fontSize: 13, color: '#2E2318', textAlign: 'left' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: c.text }} />
                    {opt.name}
                  </button>
                );
              })}
            </div>
          ) : prop.type === 'multi_select' && prop.options ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, border: `1px solid ${APP.border}`, borderRadius: 6, overflow: 'hidden', maxHeight: 160, overflowY: 'auto' }}>
              {prop.options.map((opt) => {
                const selected = Array.isArray(value) && (value as string[]).includes(opt.name);
                return (
                  <button key={opt.name} type="button" onClick={() => {
                    const current = Array.isArray(value) ? (value as string[]) : [];
                    onChange(selected ? current.filter((v) => v !== opt.name) : [...current, opt.name]);
                  }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: 'none', background: selected ? '#F5F2ED' : 'none', cursor: 'pointer', fontFamily: F, fontSize: 13, color: '#2E2318', textAlign: 'left' }}>
                    <div style={{ width: 14, height: 14, borderRadius: 3, border: selected ? 'none' : '1px solid #D4CAC0', backgroundColor: selected ? '#D97645' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {selected && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 3.5" stroke="#FFF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </div>
                    {opt.name}
                  </button>
                );
              })}
              <button type="button" onClick={() => onEdit()} style={{ padding: '6px 12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 11, color: '#D97645', fontFamily: F, textAlign: 'left' }}>Done</button>
            </div>
          ) : prop.type === 'checkbox' ? (
            <button type="button" onClick={() => onChange(!value)} style={PROP_VALUE_FILLED}>
              <span style={{ fontSize: 13, color: '#2E2318' }}>{value ? 'Checked' : 'Unchecked'}</span>
            </button>
          ) : (
            <input
              type={prop.type === 'number' ? 'number' : prop.type === 'date' ? 'date' : 'text'}
              autoFocus
              defaultValue={value != null ? String(value) : ''}
              onBlur={(e) => onChange(e.target.value || undefined)}
              onKeyDown={(e) => { if (e.key === 'Enter') onChange((e.target as HTMLInputElement).value || undefined); }}
              style={INLINE_INPUT}
              placeholder={`Enter ${prop.name.toLowerCase()}`}
            />
          )}
        </div>
      ) : (
        <button type="button" onClick={onEdit} style={PROP_VALUE_EMPTY}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 3v6M3 6h6" stroke="#B8ACA2" strokeWidth="1.2" strokeLinecap="round"/></svg>
          <span style={{ fontSize: 13, color: '#B8ACA2' }}>Set value</span>
        </button>
      )}
    </div>
  );
}

// ─── Styles ─────────────────────────────────────

const F = '"Geist", system-ui, sans-serif';

const MODAL_SHELL: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: 460,
  maxHeight: '80vh',
  borderRadius: 14,
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundColor: '#FDFCFB',
  boxShadow: 'rgba(0,0,0,0.25) 0px 16px 60px, rgba(0,0,0,0.05) 0px 0px 0px 1px',
  fontFamily: F,
};

const HEADER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '18px 20px',
  borderBottom: '1px solid #EDE8E2',
};

const CLOSE_BTN: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
};

const LABEL: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: '#9A8E82',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  lineHeight: '14px',
  fontFamily: F,
};

const PICKER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: '10px 14px',
  borderRadius: 8,
  border: '1px solid #E4DDD4',
  backgroundColor: '#FAFAF8',
  cursor: 'pointer',
  fontFamily: F,
  textAlign: 'left',
};

const DROPDOWN: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: 4,
  maxHeight: 200,
  overflowY: 'auto',
  backgroundColor: '#FDFCFB',
  border: '1px solid #E4DDD4',
  borderRadius: 8,
  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
  zIndex: 10,
  padding: 4,
};

const DROPDOWN_ITEM: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '8px 12px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  borderRadius: 6,
  fontFamily: F,
  fontSize: 13,
  color: '#2E2318',
  textAlign: 'left',
};

const TITLE_INPUT: React.CSSProperties = {
  padding: '10px 14px',
  borderRadius: 8,
  border: '1.5px solid #D97645',
  backgroundColor: '#FDFCFB',
  fontSize: 14,
  fontWeight: 400,
  color: '#2E2318',
  fontFamily: F,
  lineHeight: '18px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const PROP_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '10px 20px',
  gap: 12,
};

const PROP_LABEL: React.CSSProperties = {
  width: 120,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const PROP_VALUE_FILLED: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  padding: '7px 12px',
  borderRadius: 6,
  backgroundColor: '#F5F2ED',
  border: 'none',
  cursor: 'pointer',
  fontFamily: F,
  textAlign: 'left',
  minHeight: 30,
};

const PROP_VALUE_EMPTY: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  padding: '7px 12px',
  borderRadius: 6,
  border: '1px dashed #D4CAC0',
  background: 'none',
  gap: 6,
  cursor: 'pointer',
  fontFamily: F,
};

const INLINE_INPUT: React.CSSProperties = {
  width: '100%',
  padding: '7px 12px',
  borderRadius: 6,
  border: '1.5px solid #D97645',
  fontSize: 13,
  fontFamily: F,
  color: '#2E2318',
  outline: 'none',
  boxSizing: 'border-box',
};

const AI_HINT: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: '12px 16px',
  margin: '8px 20px 0',
  borderRadius: 8,
  backgroundColor: '#D976450A',
  border: '1px solid #D976451A',
};

const FOOTER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  borderTop: '1px solid #EDE8E2',
  marginTop: 16,
};

const SAVE_BTN: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 24px',
  borderRadius: 8,
  border: 'none',
  backgroundColor: '#2E2318',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
  color: '#FDFCFB',
  fontFamily: F,
};
